import { useState, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UploadCloud, FileSpreadsheet, Trash2, Clock, Activity, FileDown, RotateCcw } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATES = ['NSW', 'QLD', 'VIC', 'SA', 'ACT', 'TAS', 'WA'];
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 }));

const hourLabel = (h) => {
  const whole = Math.floor(h);
  const ampm = whole >= 12 ? 'pm' : 'am';
  const disp = whole % 12 === 0 ? 12 : whole % 12;
  return `${disp}${h % 1 ? ':30' : ''}${ampm}`;
};

const windowText = (w) => `${w.period === 'off_peak' ? 'Off-Peak' : w.period.charAt(0).toUpperCase() + w.period.slice(1)}: ${hourLabel(w.start_hour)}–${w.end_hour === 24 ? 'midnight' : hourLabel(w.end_hour)} ${w.days === 'weekday' ? '(weekdays)' : w.days === 'weekend' ? '(weekends)' : '(every day)'}`;

const periodBands = (shape) => {
  const bands = [];
  let start = null, current = null;
  shape.forEach((p, i) => {
    if (p.period_weekday !== current) {
      if (current === 'peak' || current === 'shoulder') bands.push({ x1: shape[start].time, x2: p.time, period: current });
      current = p.period_weekday;
      start = i;
    }
  });
  if (current === 'peak' || current === 'shoulder') bands.push({ x1: shape[start].time, x2: shape[47].time, period: current });
  return bands;
};

const ShapeCard = ({ title, subtitle, data, state }) => {
  const bands = periodBands(data.shape);
  const exportCsv = () => {
    const lines = [
      `Load Shape Export - ${title} (${state} TOU windows)`,
      `Total kWh,${data.profile ? data.profile.total_kwh : data.total_kwh}`,
      `Peak kWh,${data.buckets.peak},${data.bucket_pct.peak}%`,
      `Shoulder kWh,${data.buckets.shoulder},${data.bucket_pct.shoulder}%`,
      `Off-Peak kWh,${data.buckets.off_peak},${data.bucket_pct.off_peak}%`,
      '',
      'Time,Avg Weekday kWh,Avg Weekend kWh,Period (weekday),Period (weekend)',
      ...data.shape.map((p) => `${p.time},${p.weekday},${p.weekend},${p.period_weekday},${p.period_weekend}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `load_shape_${title.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid={`load-shape-card-${title}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            {subtitle && <CardDescription>{subtitle}</CardDescription>}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid={`load-shape-export-${title}`}><FileDown className="w-4 h-4 mr-2" />Export CSV</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[['Peak', data.buckets.peak, data.bucket_pct.peak, 'text-red-600 bg-red-50'], ['Shoulder', data.buckets.shoulder, data.bucket_pct.shoulder, 'text-amber-600 bg-amber-50'], ['Off-Peak', data.buckets.off_peak, data.bucket_pct.off_peak, 'text-emerald-600 bg-emerald-50']].map(([label, kwh, pct, cls]) => (
            <div key={label} className={`rounded-lg p-3 ${cls.split(' ')[1]}`}>
              <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
              <p className={`text-xl font-bold ${cls.split(' ')[0]}`}>{fmtNum(kwh)} kWh</p>
              <p className="text-xs text-slate-500">{pct}% of total</p>
            </div>
          ))}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.shape} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={3} />
              <YAxis tick={{ fontSize: 10 }} label={{ value: 'Avg kWh / half-hour', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#64748b' } }} />
              <Tooltip formatter={(v) => [v + ' kWh']} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {bands.map((b, i) => (
                <ReferenceArea key={i} x1={b.x1} x2={b.x2} fill={b.period === 'peak' ? '#ef4444' : '#f59e0b'} fillOpacity={0.07} />
              ))}
              <Line type="monotone" dataKey="weekday" name="Weekday avg" stroke="#003366" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="weekend" name="Weekend avg" stroke="#8CC63F" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-slate-400">Shaded bands show weekday peak (red) and shoulder (amber) windows for {state}. Weekday avg over {data.weekday_days} days, weekend avg over {data.weekend_days} days.</p>
      </CardContent>
    </Card>
  );
};

export default function LoadShape() {
  const { token } = useAuth();
  const [state, setState] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list);
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...arr.filter((f) => !names.has(f.name))];
    });
  };

  const analyse = () => {
    if (!state) { toast.error('Select a state first — it sets the peak/shoulder/off-peak hours'); return; }
    if (!files.length) { toast.error('Add at least one NEM12/interval data file'); return; }
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('state', state);
    axios.post(API_URL + '/api/optimisation/load-shape?authorization=' + token, fd, { timeout: 120000 })
      .then((r) => {
        setResult(r.data);
        (r.data.file_errors || []).forEach((e) => toast.warning(e.filename + ': ' + e.error));
        toast.success(r.data.sites.length + ' site(s) analysed');
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Analysis failed'))
      .finally(() => setUploading(false));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="load-shape-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity className="w-6 h-6 text-[#8CC63F]" /> Load Shape Analyser</h1>
            <p className="text-slate-500 mt-1">Drop NEM12 data to see the load profile and Peak / Shoulder / Off-Peak split for your state</p>
          </div>
          {result && <Button variant="outline" onClick={() => { setResult(null); setFiles([]); }} data-testid="load-shape-reset"><RotateCcw className="w-4 h-4 mr-2" />New Analysis</Button>}
        </div>

        {!result && (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="max-w-xs">
                <Label>State (sets the TOU split hours)</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger data-testid="load-shape-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#8CC63F] bg-green-50' : 'border-slate-300 hover:border-[#015492]'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                data-testid="load-shape-dropzone"
              >
                <UploadCloud className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="font-medium text-slate-700">Drag & drop NEM12 / interval data files here</p>
                <p className="text-sm text-slate-500 mt-1">.csv / .txt / .dat — single or multiple files/sites supported</p>
                <input ref={inputRef} type="file" multiple className="hidden" accept=".csv,.txt,.dat,.nem,.nem12,.mdf" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="load-shape-file-input" />
              </div>

              {files.length > 0 && (
                <div className="space-y-2" data-testid="load-shape-file-list">
                  {files.map((f) => (
                    <div key={f.name} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                      <span className="flex-1 text-sm text-slate-700 truncate">{f.name}</span>
                      <button onClick={() => setFiles((prev) => prev.filter((x) => x.name !== f.name))} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

              <Button onClick={analyse} disabled={uploading} className="w-full bg-[#015492] hover:bg-[#013f6e]" data-testid="load-shape-analyse-btn">
                {uploading ? (<><Clock className="w-4 h-4 mr-2 animate-spin" />Analysing...</>) : (<><Activity className="w-4 h-4 mr-2" />Generate Load Shape</>)}
              </Button>
            </CardContent>
          </Card>
        )}

        {result && (
          <div className="space-y-5">
            <Card className="bg-slate-50" data-testid="load-shape-windows">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[#003366] text-white">{result.state} TOU windows</Badge>
                  {result.windows.map((w, i) => (
                    <Badge key={i} variant="outline" className={w.period === 'peak' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}>{windowText(w)}</Badge>
                  ))}
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700">Off-Peak: all remaining hours</Badge>
                </div>
              </CardContent>
            </Card>

            {result.combined && (
              <ShapeCard title="All Sites Combined" subtitle={`${result.sites.length} sites • ${fmtNum(result.combined.total_kwh)} kWh total`} data={result.combined} state={result.state} />
            )}

            {result.sites.map((s) => (
              <ShapeCard
                key={s.nmi}
                title={s.nmi}
                subtitle={`${s.files.join(', ')} • ${s.profile.start_date} – ${s.profile.end_date} (${s.profile.days} days) • ${fmtNum(s.profile.total_kwh)} kWh • max ${s.profile.max_demand_kw} kW`}
                data={s}
                state={result.state}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
